import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Users, MessageSquare, MoreVertical, Loader2 } from 'lucide-react';
import { communitiesApi } from '../../api';
import { Button } from '../ui/button';
import DashboardLayout from './DashboardLayout';
import CreateCommunityModal from '../modals/CreateCommunityModal';

const Communities = () => {
  const { t } = useTranslation();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchCommunities = async () => {
    try {
      const response = await communitiesApi.list(0, 50);
      setCommunities(response.data);
    } catch (error) {
      console.error('Failed to fetch communities:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommunities();
  }, []);

  const handleCommunityCreated = (newCommunity) => {
    setCommunities(prev => [newCommunity, ...prev]);
  };

  const filteredCommunities = communities.filter(community => {
    const matchesSearch = community.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Create Modal */}
      <CreateCommunityModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCommunityCreated}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('communities.title')}</h1>
          <p className="text-gray-500 mt-1">{t('communities.subtitle')}</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('communities.create_btn')}
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('communities.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Community cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCommunities.map((community) => (
          <Link
            to={`/dashboard/community/${community.id}`}
            key={community.id}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group block"
          >
            {/* Cover image */}
            <div className="relative h-32 overflow-hidden">
              <img
                src={community.image || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&q=80'}
                alt={community.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              {community.is_public && (
                <div className="absolute top-3 right-3">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
                    {t('communities.public')}
                  </span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-5">
              <div className="flex items-start justify-between">
                <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                  {community.name}
                </h3>
                <button className="p-1 text-gray-400 hover:text-gray-600">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                {community.description}
              </p>
              
              {/* Stats */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                  <Users className="w-4 h-4" />
                  <span>{(community.members_count || 0).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                  <MessageSquare className="w-4 h-4" />
                  <span>{(community.posts_count || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {filteredCommunities.length === 0 && !loading && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('communities.empty_title')}</h3>
          <p className="text-gray-500">{t('communities.empty_subtitle')}</p>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Communities;
